'use client'

import { forwardRef, HTMLAttributes, useState } from 'react'
import {
    CheckSquare,
    Download,
    EllipsisVertical,
    LucideIcon,
    MessageSquarePlus,
    TrendingUp,
} from 'lucide-react'
import { IResearchedAd } from '@/api/research-jobs/ads/get-researched-ads-query'
import { DropdownActionsContent } from '@/components/dropdown-action/dropdown-actions-container'
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { SceneGenerationStepResultCardButton } from '@/features/generations/generation-result/scene-generation-step-result-card-button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { GrowthIndicator } from '@/features/research/research-ads/legacy/growth-indicator'
import { useResearchAdActions } from '@/features/research/research-ads/research-ad-card/utils/use-research-ad-actions'
import { cn } from '@/utils/styles/cn'

/**
 * Hover overlay for the research ad card, mirroring the creatives-card action layout so overlapping
 * actions sit in the same position on both surfaces: TOP-LEFT holds the general one-click actions
 * (download and attach to chat, matching the creative card); TOP-RIGHT holds the ⋯ menu with the
 * rest (save to drive, view original, view growth). Save-to-board lives in the card footer as the
 * split save button — not an overlay action. The menu trigger stays visible while its menu is open
 * so the menu never floats over a vanished anchor.
 */
export function ResearchAdCardHoverActions({
    ad,
    visible,
    onEnterSelection,
}: {
    ad: IResearchedAd
    visible: boolean
    onEnterSelection?: () => void
}) {
    const { download, canDownload, attachToChat } = useResearchAdActions(ad)

    return (
        <>
            <div
                className="absolute left-[12px] top-[12px] z-20 flex items-center justify-start gap-2"
                onClick={(event) => event.stopPropagation()}
            >
                <SceneGenerationStepResultCardButton
                    hidden={!visible}
                    Icon={Download}
                    disabled={!canDownload}
                    tooltip="Download"
                    onClick={download}
                />
                <SceneGenerationStepResultCardButton
                    hidden={!visible}
                    Icon={MessageSquarePlus}
                    tooltip="Attach to chat"
                    onClick={attachToChat}
                />
            </div>

            <div
                className="absolute right-[12px] top-[12px] z-20 flex items-center justify-end gap-2"
                onClick={(event) => event.stopPropagation()}
            >
                <ResearchAdCardActionsMenu ad={ad} visible={visible} onEnterSelection={onEnterSelection} />
            </div>
        </>
    )
}

/** The ⋯ overflow menu with the remaining actions, mirroring `CreativeActionsMenu`'s card variant. */
function ResearchAdCardActionsMenu({
    ad,
    visible,
    onEnterSelection,
}: {
    ad: IResearchedAd
    visible: boolean
    onEnterSelection?: () => void
}) {
    const [open, setOpen] = useState(false)
    const [growthChartOpen, setGrowthChartOpen] = useState(false)

    const { menu } = useResearchAdActions(ad)
    const hasGrowthData = Boolean(ad.metrics.trendMetrics?.growth)

    return (
        <>
            <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
                <DropdownMenuTrigger asChild>
                    <ResearchAdOverlayPopoverTrigger
                        Icon={EllipsisVertical}
                        label="More actions"
                        visible={visible}
                        open={open}
                    />
                </DropdownMenuTrigger>

                <DropdownActionsContent>
                    {menu.map((action) => {
                        const Icon = action.Icon
                        return (
                            <DropdownMenuItem
                                key={action.key}
                                onClick={() => {
                                    action.onClick()
                                    setOpen(false)
                                }}
                                disabled={action.disabled}
                                className="flex items-center gap-2"
                            >
                                <Icon className="size-4" />
                                <span>{action.label}</span>
                            </DropdownMenuItem>
                        )
                    })}
                    <DropdownMenuItem
                        onClick={() => {
                            setGrowthChartOpen(true)
                            setOpen(false)
                        }}
                        disabled={!hasGrowthData}
                        className="flex items-center gap-2"
                    >
                        <TrendingUp className="size-4" />
                        <span>{hasGrowthData ? 'View growth over time' : 'Growth data still collecting'}</span>
                    </DropdownMenuItem>
                    {onEnterSelection && (
                        <DropdownMenuItem
                            onClick={() => {
                                onEnterSelection()
                                setOpen(false)
                            }}
                            className="flex items-center gap-2"
                        >
                            <CheckSquare className="size-4" />
                            <span>Select</span>
                        </DropdownMenuItem>
                    )}
                </DropdownActionsContent>
            </DropdownMenu>

            <GrowthIndicator
                adUuid={ad.uuid}
                trendMetrics={ad.metrics.trendMetrics}
                variant="modal-only"
                open={growthChartOpen}
                onOpenChange={setGrowthChartOpen}
            />
        </>
    )
}

/**
 * Glass-chrome trigger for popover/dropdown actions in the overlay (the `CreativeActionsMenu` card
 * pattern) — forwards ref + props so Radix `asChild` can wire it, staying visible while its menu is
 * open. The tooltip lives inside the component (forced closed while the menu is open) because the
 * host's `PopoverTrigger asChild` must receive this component directly, not a Tooltip root.
 */
const ResearchAdOverlayPopoverTrigger = forwardRef<
    HTMLDivElement,
    { Icon: LucideIcon; label: string; visible: boolean; open: boolean } & HTMLAttributes<HTMLDivElement>
>(({ Icon, label, visible, open, className, ...props }, ref) => (
    <Tooltip delayDuration={0} open={open ? false : undefined}>
        <TooltipTrigger asChild>
            <div
                ref={ref}
                role="button"
                aria-label={label}
                {...props}
                className={cn(
                    'flex h-5 w-5 cursor-pointer items-center justify-center transition-opacity duration-150',
                    !visible && !open ? 'pointer-events-none opacity-0' : 'opacity-100',
                    className
                )}
            >
                <div
                    className={cn(
                        'group/selector relative z-0 flex size-5 shrink-0 select-none items-center justify-center overflow-hidden rounded-[6px]',
                        'outline outline-offset-0.5 outline-white/60 transition-all duration-150',
                        'cursor-pointer bg-white/50 hover:bg-[#FF631B] hover:outline-[#BF6323]/90 hover:brightness-[1.1]',
                        open && 'bg-[#FF631B] outline-[#BF6323]/90'
                    )}
                >
                    <Icon
                        className={cn(
                            'size-4 stroke-black/90 group-hover/selector:stroke-white/90',
                            open && 'stroke-white/90'
                        )}
                    />
                </div>
            </div>
        </TooltipTrigger>
        <TooltipContent sideOffset={6}>{label}</TooltipContent>
    </Tooltip>
))
ResearchAdOverlayPopoverTrigger.displayName = 'ResearchAdOverlayPopoverTrigger'
